import React, { useEffect, useState } from "react";
import { Loader2, Save, Plus, Trash2, Instagram } from "lucide-react";
import { adminApi } from "@/admin/adminApi";
import { uploadMedia, absUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const Section = ({ title, desc, children }) => (
  <div className="rounded-wp-lg border border-border bg-card p-5 shadow-wp-soft">
    <h2 className="font-display text-lg">{title}</h2>
    {desc && <p className="mb-4 mt-0.5 text-sm text-wp-ink-muted">{desc}</p>}
    <div className={desc ? "" : "mt-4"}>{children}</div>
  </div>
);

export default function Homepage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.get("/settings").then((r) => setS(r.data || {})).catch(() => setS({})); }, []);

  const setField = (path, value) => {
    setS((prev) => {
      const keys = path.split(".");
      const clone = JSON.parse(JSON.stringify(prev || {}));
      let cur = clone;
      for (let i = 0; i < keys.length - 1; i++) { cur[keys[i]] = cur[keys[i]] || {}; cur = cur[keys[i]]; }
      cur[keys[keys.length - 1]] = value;
      return clone;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { key, seed_version, _id, ...payload } = s || {};
      const { data } = await adminApi.put("/settings", payload);
      setS(data);
      toast.success("Settings saved \uD83D\uDC9D");
    } catch { toast.error("Could not save settings"); }
    finally { setSaving(false); }
  };

  const addTile = () => {
    const tiles = [...(s?.instagram?.tiles || [])];
    tiles.push({ id: `ig${Date.now()}`, image: "", caption: "", likes: 0 });
    setField("instagram.tiles", tiles);
  };
  const updateTile = (i, patch) => {
    const tiles = [...(s?.instagram?.tiles || [])];
    tiles[i] = { ...tiles[i], ...patch };
    setField("instagram.tiles", tiles);
  };
  const removeTile = (i) => setField("instagram.tiles", (s?.instagram?.tiles || []).filter((_, idx) => idx !== i));
  const uploadTile = async (i, file) => {
    try { const data = await uploadMedia(file, "photo", "admin"); updateTile(i, { image: absUrl(data.media_url) }); }
    catch { toast.error("Upload failed"); }
  };

  // ----- Corporate gifting helpers -----
  const updateCat = (i, patch) => {
    const cats = [...(s?.corporate?.categories || [])];
    cats[i] = { ...cats[i], ...patch };
    setField("corporate.categories", cats);
  };
  const uploadCatImage = async (i, file) => {
    try { const data = await uploadMedia(file, "photo", "admin"); updateCat(i, { image: absUrl(data.media_url) }); }
    catch { toast.error("Upload failed"); }
  };

  // ----- Custom Silver helpers -----
  const updateSample = (i, patch) => {
    const arr = [...(s?.custom_silver?.samples || [])];
    arr[i] = { ...arr[i], ...patch };
    setField("custom_silver.samples", arr);
  };
  const uploadSample = async (i, file) => {
    try { const data = await uploadMedia(file, "photo", "admin"); updateSample(i, { image: absUrl(data.media_url) }); }
    catch { toast.error("Upload failed"); }
  };
  const updateStep = (i, patch) => {
    const arr = [...(s?.custom_silver?.how_it_works || [])];
    arr[i] = { ...arr[i], ...patch };
    setField("custom_silver.how_it_works", arr);
  };

  if (!s) return <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 w-full rounded-wp-lg" />)}</div>;

  const flags = s.feature_flags || {};
  const corp = s.corporate || {};
  const silver = s.custom_silver || {};

  return (
    <div data-testid="admin-homepage">
      <div className="mb-5 flex items-center justify-between">
        <div><h1 className="font-display text-2xl">Homepage &amp; Settings</h1><p className="text-sm text-wp-ink-muted">Edit hero copy, announcement, Instagram feed and feature flags.</p></div>
        <Button onClick={save} disabled={saving} className="rounded-xl bg-wp-rose-strong text-white hover:brightness-105" data-testid="settings-save-button">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save changes
        </Button>
      </div>

      <div className="space-y-5">
        <Section title="Announcement bar">
          <Input value={s.announcement || ""} onChange={(e) => setField("announcement", e.target.value)} className="rounded-wp-md" data-testid="settings-announcement" />
        </Section>

        <Section title="Hero" desc="The first thing visitors see.">
          <div className="space-y-3">
            <div><Label className="mb-1.5 block text-sm">Headline</Label><Input value={s.hero?.headline || ""} onChange={(e) => setField("hero.headline", e.target.value)} className="rounded-wp-md" data-testid="settings-hero-headline" /></div>
            <div><Label className="mb-1.5 block text-sm">Subheading</Label><Textarea rows={2} value={s.hero?.subheading || ""} onChange={(e) => setField("hero.subheading", e.target.value)} className="rounded-wp-md" data-testid="settings-hero-subheading" /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label className="mb-1.5 block text-sm">Primary CTA</Label><Input value={s.hero?.primary_cta || ""} onChange={(e) => setField("hero.primary_cta", e.target.value)} className="rounded-wp-md" /></div>
              <div><Label className="mb-1.5 block text-sm">Secondary CTA</Label><Input value={s.hero?.secondary_cta || ""} onChange={(e) => setField("hero.secondary_cta", e.target.value)} className="rounded-wp-md" /></div>
            </div>
          </div>
        </Section>

        <Section title="Contact">
          <div><Label className="mb-1.5 block text-sm">WhatsApp number (with country code)</Label><Input value={s.whatsapp_number || ""} onChange={(e) => setField("whatsapp_number", e.target.value)} className="rounded-wp-md" data-testid="settings-whatsapp" /></div>
        </Section>

        <Section title="Commerce" desc="Flat shipping charge, and the minimum merchandise value for each packaging option (shipping is not counted toward the minimum).">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="mb-1.5 block text-sm">Standard shipping charge (₹)</Label>
              <Input type="number" min="0" value={s.shipping_charge ?? 85} onChange={(e) => setField("shipping_charge", Number(e.target.value))} className="rounded-wp-md" data-testid="settings-shipping-charge" />
              <p className="mt-1 text-xs text-wp-ink-muted">Enter 0 for free shipping.</p>
            </div>
            <div>
              <Label className="mb-1.5 block text-sm">Standard packaging min (₹)</Label>
              <Input type="number" min="0" value={s.min_order_value ?? 449} onChange={(e) => setField("min_order_value", Number(e.target.value))} className="rounded-wp-md" data-testid="settings-min-order-value" />
              <p className="mt-1 text-xs text-wp-ink-muted">Standard Gift Packaging minimum.</p>
            </div>
            <div>
              <Label className="mb-1.5 block text-sm">Signature packaging min (₹)</Label>
              <Input type="number" min="0" value={s.signature_min_order_value ?? 999} onChange={(e) => setField("signature_min_order_value", Number(e.target.value))} className="rounded-wp-md" data-testid="settings-signature-min-order-value" />
              <p className="mt-1 text-xs text-wp-ink-muted">Signature Wonder Parcel Box minimum.</p>
            </div>
          </div>
        </Section>

        <Section title="Instagram" desc="Toggle the feed, set your handle, and manage the tiles shown on the homepage.">
          <div className="flex items-center justify-between rounded-wp-md border border-border px-3 py-2.5">
            <Label className="text-sm">Show Instagram section</Label>
            <Switch checked={s.instagram?.enabled !== false} onCheckedChange={(v) => setField("instagram.enabled", v)} data-testid="settings-instagram-enabled" />
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div><Label className="mb-1.5 block text-sm">Profile URL</Label><Input value={s.instagram?.url || ""} onChange={(e) => setField("instagram.url", e.target.value)} className="rounded-wp-md" /></div>
            <div><Label className="mb-1.5 block text-sm">Handle</Label><Input value={s.instagram?.handle || ""} onChange={(e) => setField("instagram.handle", e.target.value)} className="rounded-wp-md" /></div>
          </div>
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between"><Label className="text-sm">Feed tiles</Label><Button size="sm" variant="outline" className="rounded-lg" onClick={addTile}><Plus className="mr-1.5 h-3.5 w-3.5" /> Add tile</Button></div>
            <div className="grid gap-3 sm:grid-cols-2">
              {(s.instagram?.tiles || []).map((t, i) => (
                <div key={t.id || i} className="flex gap-3 rounded-wp-md border border-border p-3">
                  <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md bg-wp-cream-100">{t.image ? <img src={t.image} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-wp-ink-muted"><Instagram className="h-4 w-4" /></div>}</div>
                  <div className="flex-1 space-y-2">
                    <Input value={t.image || ""} onChange={(e) => updateTile(i, { image: e.target.value })} placeholder="Image URL" className="h-8 rounded-md text-xs" />
                    <Input value={t.caption || ""} onChange={(e) => updateTile(i, { caption: e.target.value })} placeholder="Caption" className="h-8 rounded-md text-xs" />
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer text-xs font-medium text-wp-ocean hover:underline">Upload<input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadTile(i, e.target.files[0])} /></label>
                      <button onClick={() => removeTile(i)} className="ml-auto text-wp-rose-strong"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section title="Corporate Gifting" desc="Manage the /corporate-gifting page: enable it, edit copy, budgets and the five gift concepts (image, title, copy, visibility).">
          <div className="flex items-center justify-between rounded-wp-md border border-border px-3 py-2.5">
            <Label className="text-sm">Show Corporate Gifting page</Label>
            <Switch checked={corp.enabled !== false} onCheckedChange={(v) => setField("corporate.enabled", v)} data-testid="settings-corporate-enabled" />
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div><Label className="mb-1.5 block text-sm">Headline line 1</Label><Input value={corp.headline_line1 || ""} onChange={(e) => setField("corporate.headline_line1", e.target.value)} className="rounded-wp-md" data-testid="settings-corporate-headline1" /></div>
            <div><Label className="mb-1.5 block text-sm">Headline line 2</Label><Input value={corp.headline_line2 || ""} onChange={(e) => setField("corporate.headline_line2", e.target.value)} className="rounded-wp-md" data-testid="settings-corporate-headline2" /></div>
          </div>
          <div className="mt-3"><Label className="mb-1.5 block text-sm">Subheading</Label><Textarea rows={2} value={corp.subheading || ""} onChange={(e) => setField("corporate.subheading", e.target.value)} className="rounded-wp-md" data-testid="settings-corporate-subheading" /></div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div><Label className="mb-1.5 block text-sm">Primary CTA</Label><Input value={corp.primary_cta || ""} onChange={(e) => setField("corporate.primary_cta", e.target.value)} className="rounded-wp-md" /></div>
            <div><Label className="mb-1.5 block text-sm">Sample imagery note</Label><Input value={corp.sample_note || ""} onChange={(e) => setField("corporate.sample_note", e.target.value)} className="rounded-wp-md" /></div>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div><Label className="mb-1.5 block text-sm">Bulk section title</Label><Input value={corp.bulk_title || ""} onChange={(e) => setField("corporate.bulk_title", e.target.value)} className="rounded-wp-md" /></div>
            <div><Label className="mb-1.5 block text-sm">Bulk section copy</Label><Input value={corp.bulk_copy || ""} onChange={(e) => setField("corporate.bulk_copy", e.target.value)} className="rounded-wp-md" /></div>
          </div>
          <div className="mt-3">
            <Label className="mb-1.5 block text-sm">Budget ranges (one per line)</Label>
            <Textarea rows={6} value={(corp.budgets || []).join("\n")} onChange={(e) => setField("corporate.budgets", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className="rounded-wp-md" data-testid="settings-corporate-budgets" />
          </div>

          <div className="mt-4">
            <Label className="mb-2 block text-sm">Gift concepts</Label>
            <div className="space-y-3">
              {(corp.categories || []).map((cat, i) => (
                <div key={cat.id || i} className="rounded-wp-md border border-border p-3" data-testid={`settings-corporate-cat-${cat.id || i}`}>
                  <div className="flex gap-3">
                    <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md bg-wp-cream-100">
                      {cat.image ? <img src={cat.image} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-xs text-wp-ink-muted">No image</div>}
                    </div>
                    <div className="flex-1 space-y-2">
                      <Input value={cat.title || ""} onChange={(e) => updateCat(i, { title: e.target.value })} placeholder="Title" className="h-8 rounded-md text-sm" />
                      <Textarea rows={2} value={cat.copy || ""} onChange={(e) => updateCat(i, { copy: e.target.value })} placeholder="Copy" className="rounded-md text-xs" />
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="cursor-pointer text-xs font-medium text-wp-ocean hover:underline">Replace image<input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadCatImage(i, e.target.files[0])} data-testid={`settings-corporate-cat-upload-${cat.id || i}`} /></label>
                        <div className="ml-auto flex items-center gap-2">
                          <Label className="text-xs text-wp-ink-muted">Visible</Label>
                          <Switch checked={cat.visible !== false} onCheckedChange={(v) => updateCat(i, { visible: v })} data-testid={`settings-corporate-cat-visible-${cat.id || i}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section title="Custom Silver Gifts" desc="Manage the /custom-silver page: enable it, edit copy, pricing, sample images, How It Works and certification/hallmarking wording.">
          <div className="flex items-center justify-between rounded-wp-md border border-border px-3 py-2.5">
            <Label className="text-sm">Show Custom Silver Gifts page</Label>
            <Switch checked={silver.enabled !== false} onCheckedChange={(v) => setField("custom_silver.enabled", v)} data-testid="settings-silver-enabled" />
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div><Label className="mb-1.5 block text-sm">Headline</Label><Input value={silver.headline || ""} onChange={(e) => setField("custom_silver.headline", e.target.value)} className="rounded-wp-md" data-testid="settings-silver-headline" /></div>
            <div><Label className="mb-1.5 block text-sm">Supporting line</Label><Input value={silver.supporting_line || ""} onChange={(e) => setField("custom_silver.supporting_line", e.target.value)} className="rounded-wp-md" /></div>
          </div>
          <div className="mt-3"><Label className="mb-1.5 block text-sm">Subheading</Label><Textarea rows={2} value={silver.subheading || ""} onChange={(e) => setField("custom_silver.subheading", e.target.value)} className="rounded-wp-md" data-testid="settings-silver-subheading" /></div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div><Label className="mb-1.5 block text-sm">Starting price text</Label><Input value={silver.starting_price_text || ""} onChange={(e) => setField("custom_silver.starting_price_text", e.target.value)} className="rounded-wp-md" data-testid="settings-silver-price" /></div>
            <div><Label className="mb-1.5 block text-sm">Price note</Label><Input value={silver.price_note || ""} onChange={(e) => setField("custom_silver.price_note", e.target.value)} className="rounded-wp-md" /></div>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div><Label className="mb-1.5 block text-sm">Primary CTA</Label><Input value={silver.primary_cta || ""} onChange={(e) => setField("custom_silver.primary_cta", e.target.value)} className="rounded-wp-md" /></div>
            <div><Label className="mb-1.5 block text-sm">Secondary CTA</Label><Input value={silver.secondary_cta || ""} onChange={(e) => setField("custom_silver.secondary_cta", e.target.value)} className="rounded-wp-md" /></div>
          </div>
          <div className="mt-3"><Label className="mb-1.5 block text-sm">Certification / hallmarking text</Label><Textarea rows={2} value={silver.certification_text || ""} onChange={(e) => setField("custom_silver.certification_text", e.target.value)} className="rounded-wp-md" data-testid="settings-silver-certification" /></div>
          <div className="mt-3"><Label className="mb-1.5 block text-sm">Manufacturing disclaimer</Label><Textarea rows={2} value={silver.manufacturing_disclaimer || ""} onChange={(e) => setField("custom_silver.manufacturing_disclaimer", e.target.value)} className="rounded-wp-md" /></div>

          <div className="mt-4">
            <Label className="mb-2 block text-sm">Sample images</Label>
            <div className="grid gap-3 sm:grid-cols-3">
              {(silver.samples || []).map((sm, i) => (
                <div key={i} className="rounded-wp-md border border-border p-3" data-testid={`settings-silver-sample-${i}`}>
                  <div className="mb-2 aspect-square overflow-hidden rounded-md bg-wp-cream-100">{sm.image ? <img src={sm.image} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-xs text-wp-ink-muted">No image</div>}</div>
                  <Input value={sm.label || ""} onChange={(e) => updateSample(i, { label: e.target.value })} placeholder="Label" className="h-8 rounded-md text-xs" />
                  <label className="mt-2 block cursor-pointer text-xs font-medium text-wp-ocean hover:underline">Replace image<input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadSample(i, e.target.files[0])} data-testid={`settings-silver-sample-upload-${i}`} /></label>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <Label className="mb-2 block text-sm">How It Works steps</Label>
            <div className="space-y-2">
              {(silver.how_it_works || []).map((st, i) => (
                <div key={i} className="grid gap-2 rounded-wp-md border border-border p-3 sm:grid-cols-[180px_1fr]" data-testid={`settings-silver-step-${i}`}>
                  <Input value={st.title || ""} onChange={(e) => updateStep(i, { title: e.target.value })} placeholder="Step title" className="h-8 rounded-md text-sm" />
                  <Input value={st.copy || ""} onChange={(e) => updateStep(i, { copy: e.target.value })} placeholder="Step copy" className="h-8 rounded-md text-sm" />
                </div>
              ))}
            </div>
          </div>
        </Section>

        {Object.keys(flags).length > 0 && (
          <Section title="Feature flags" desc="Enable or disable storefront modules.">
            <div className="grid gap-2 sm:grid-cols-2">
              {Object.keys(flags).map((k) => (
                <div key={k} className="flex items-center justify-between rounded-wp-md border border-border px-3 py-2.5">
                  <Label className="text-sm capitalize">{k.replace(/_/g, " ")}</Label>
                  <Switch checked={flags[k] !== false} onCheckedChange={(v) => setField(`feature_flags.${k}`, v)} data-testid={`settings-flag-${k}`} />
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
